import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import {
  Button,
  Stack,
  type StackProps,
  styled,
  Typography,
} from "@mui/material";
import { type ReactNode, useRef } from "react";

import { AnimatedGradientBorder } from "../../animated-gradient-border/AnimatedGradientBorder";
import { AskMaraSvg } from "../../assets/svg";
import { MagicStarsAnimation } from "../../magic-stars-animation/MagicStarsAnimation";
import { Markdown } from "../../markdown/Markdown";

export type AskMaraCardSize = "small" | "medium";

export interface AskMaraCardAction {
  /** Text label displayed inside the action button. Defaults to "Descubre cómo" if omitted. */
  label?: string;
  /** Click handler executed when the action button is triggered. */
  onClick: () => void;
  /** Optional variant or disabled state if supported by Figma specs. */
  disabled?: boolean;
}

export interface AskMaraCardProps extends StackProps {
  /** Card title (supports Markdown). Styled according to Figma title tokens. */
  title: string;
  /** Main card header icon. Defaults to <AskMaraSvg/>. */
  icon?: ReactNode;
  /** Optional detailed description (supports Markdown). Omitted from DOM if undefined/empty. */
  description?: string;
  /** Optional metric or display value (supports string or number including 0). Omitted from DOM if undefined. */
  value?: string | number;
  /** Optional icon displayed inside the value block before the prefix/value. */
  valueIcon?: ReactNode;
  /** Prefix string displayed before the value (e.g., "$", "+"). */
  prefix?: string;
  /** Unit of measurement displayed after the value (e.g., "USD", "kg", "%"). */
  unit?: string;
  /** Optional action object. If provided, renders the primary card action button with a default label of "Descubre cómo". */
  action?: AskMaraCardAction;
  /** Internal padding variant ("small": 8px, "medium": 16px; defaults to "small", per Figma spec). */
  size?: AskMaraCardSize;
  /** Toggles the spotlight border animation effect per Figma tokens (defaults to true). */
  hasSpotlightBorder?: boolean;
  /** Toggles the sparkle animation over the value (defaults to true). */
  hasMagicStars?: boolean;
}

const WavyBorderStack = styled(AnimatedGradientBorder, {
  shouldForwardProp: (prop) => prop !== "size",
})<{
  size: AskMaraCardSize;
}>(({ size }) => ({
  padding: size === "small" ? "8px" : "16px",
  borderRadius: 12,
  width: "100%",
  gap: size === "small" ? "4px" : "8px",
}));

const PlainBorderStack = styled(Stack)<{
  size: AskMaraCardSize;
}>(({ theme, size }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: size === "small" ? "8px" : "16px",
  borderRadius: 12,
  width: "fit-content",
  gap: size === "small" ? "4px" : "8px",
}));

export function AskMaraCard({
  title,
  icon = <AskMaraSvg />,
  description,
  value,
  valueIcon,
  prefix,
  unit,
  action,
  size = "small",
  hasSpotlightBorder = true,
  hasMagicStars = false,
  sx,
  ...props
}: AskMaraCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const Container = hasSpotlightBorder ? WavyBorderStack : PlainBorderStack;
  const containerExtraProps = hasSpotlightBorder
    ? { borderWidth: "1px" }
    : undefined;

  return (
    <Container
      ref={cardRef}
      size={size}
      sx={sx}
      {...containerExtraProps}
      {...props}
    >
      <Stack
        direction="row"
        gap={1}
        justifyContent="space-between"
        alignItems="center"
        alignSelf="stretch"
      >
        <Stack
          direction="row"
          gap={0.5}
          // Covers both MUI icons (.MuiSvgIcon-root) and raw svgr imports like AskMaraSvg
          sx={(theme) => ({
            "& svg": {
              fontSize: theme.typography.body2.fontSize,
              width: theme.typography.body2.fontSize,
              height: theme.typography.body2.fontSize,
              flexShrink: 0,
            },
          })}
          alignItems="start"
        >
          <Stack component="span" aria-hidden="true" sx={{ padding: 0.25 }}>
            {icon}
          </Stack>
          <Markdown
            variant={size === "small" ? "caption" : "body2"}
            color="secondary.dark"
          >
            {title}
          </Markdown>
        </Stack>
        {value !== undefined && value !== null && value !== "" && (
          <Stack
            direction="row"
            sx={(theme) => ({
              color: theme.palette.primary.main,
              "& .MuiSvgIcon-root": {
                fontSize: theme.typography.caption.fontSize,
              },
            })}
            alignItems="center"
          >
            <Stack direction="row" gap={0.25} alignItems="center">
              {valueIcon}

              <Stack direction="row" alignItems="center">
                <Typography variant="overline" color="primary.light">
                  {prefix}
                </Typography>
                <Typography
                  variant="h5"
                  color="inherit"
                  sx={{ position: "relative" }}
                >
                  {hasMagicStars && (
                    <MagicStarsAnimation boundaryRef={cardRef} />
                  )}
                  {value}
                </Typography>
                <Typography variant="overline" color="primary.light">
                  {unit}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
      {action && (
        <Stack alignItems="flex-start">
          <Button
            onClick={action.onClick}
            disabled={action.disabled}
            sx={(theme) => ({
              padding: "4px 10px",
              ":hover": { backgroundColor: theme.palette.background.paper },
            })}
          >
            <Typography
              component="span"
              sx={(theme) => ({
                backgroundImage: theme.palette.gradient.outline,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              })}
            >
              <Stack
                component="span"
                direction="row"
                gap={0.5}
                alignItems="center"
              >
                <Typography variant="caption" color="inherit">
                  {action.label ?? "Descubre cómo"}
                </Typography>
                {/* Solid accent color: matches the gradient's end color since the icon sits after the text */}
                <ChevronRightOutlinedIcon
                  sx={(theme) => ({
                    fontSize: theme.typography.body1.fontSize,
                    color: theme.palette.accent.main,
                  })}
                />
              </Stack>
            </Typography>
          </Button>
        </Stack>
      )}
      {description && <Markdown>{description}</Markdown>}
    </Container>
  );
}
